import React from "react";
import {TextField, Theme} from "@mui/material";
import {makeStyles} from "@mui/styles";

interface StyleProps {
    readonly limit: number
    readonly percent: number | undefined
    readonly error: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: "relative",
        flex: 1,
        "& .MuiInputAdornment-root": {
            margin: 0
        },
        "& .MuiOutlinedInput-input": {
            fontSize: theme.typography.pxToRem(14),
            padding: (props: StyleProps) => `12px ${props.limit ? "48px" : "12px"} 12px  12px`,
            color: "#000",
            borderRadius: theme.typography.pxToRem(8),
            transition: "0.3s",
        },
        "& .MuiOutlinedInput-root ": {
            borderRadius: theme.typography.pxToRem(8),
            background: "#F2F2F2",
            border: "2px solid transparent",
            boxShadow: "none",
            "&:focus": {
                boxShadow: "rgb(19, 107, 245) 0 0 0 2px"
            },
            "&:not(:focus):hover": {
                boxShadow: "#e1e1e1  0 0 0 2px"
            }

        },
        "& .MuiOutlinedInput-notchedOutline ": {
            border: "none",
        },
        "& .MuiFormHelperText-root": {
            marginLeft: 0,
        },
    },
    label: {
        marginBottom: theme.typography.pxToRem(5),
        fontSize: theme.typography.pxToRem(12),
        display: "block",
        fontWeight: 700,
        color: "#000"
    },
    required: {
        color: "#EA6B44"
    },
    optional: {
        color: "#9a9a9a"
    },
    helpText: {
        fontSize: 11,
        marginTop: theme.typography.pxToRem(8),
        color: "#757575"
    },
    limit: {
        minWidth: 34,
        maxWidth: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        border: "1px solid #fff",
        justifyContent: "center",
        background: (props: StyleProps) => `conic-gradient(${props.error ? "#3F51B5": "#FF5963"} ${props.percent ?? 0}%, transparent ${props.percent ?? 0}%)`,
        borderRadius: "50%",
        '& .value-length': {
            fontSize: 13,
            color: (props: StyleProps) => props.error ? "#3F51B5": "#FF5963",
            position: "absolute"
        },
        "&:before": {
            content: "''",
            background: "#f2f2f2",
            width: "89%",
            borderRadius: "50%",
            height: "89%",
            position: "relative",
        }
    }
}));

interface Props {
    readonly placeholder?: string;
    readonly name: string;
    readonly labelText?: string
    readonly required?: boolean
    readonly limit?: number
    readonly valueLength?: number
    readonly helpDesc?: string,

    readonly [key: string]: any
}

export function AppField({placeholder, name, icon, labelText, helpDesc, limit, required, valueLength = 0, ...props}: Props) {
    const classes = useStyles({
        limit: limit ?? 0,
        percent: limit && (valueLength >= limit ? 100 : valueLength / limit * 100),
        error: valueLength <= (limit ?? 0)
    });
    return (
        <div className={classes.root}>
            {labelText && (
                <label className={classes.label}>
                    {labelText}
                    &nbsp;
                    {required ? <span className={classes.required}>(required)</span> :
                        <span className={classes.optional}>(optional)</span>}
                </label>
            )}
            <TextField
                fullWidth
                placeholder={placeholder}
                className={classes.root}
                id={name}
                name={name}
                {...props}
                variant="outlined"
                InputProps={limit ? {
                    endAdornment: <div className={classes.limit}>
                        <span className="value-length">{valueLength}</span>
                    </div>
                } : {}}
            />
            {helpDesc && <p className={classes.helpText}>{helpDesc}</p>}
        </div>
    );
}
